import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SelectionView, { SelectionInvalid, SelectionExpired } from '@/components/SelectionView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientSelection() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const fetchPage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/select/${token}`);
      setData(data);
      if (data?.selected_items?.length) setSelected(data.selected_items);
      if (data?.selections_submitted_at) setSavedAt(data.selections_submitted_at);
    } catch (e) {
      setError(e.response?.status === 404 ? 'invalid' : 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const toggle = (id) => {
    if (data?.is_locked) return;
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      const optional = prev.filter(pid => {
        const k = data.keepsakes.find(kk => kk.id === pid);
        return k && !k.is_always_included;
      });
      if (optional.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const submit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data: res } = await axios.post(`${API}/select/${token}`, { selected_item_ids: selected });
      setSelected(res.selected_items);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Could not save your selections');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A89F91] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'invalid' || !data) return <SelectionInvalid />;
  if (data.expired) return <SelectionExpired interviewerName={data.interviewer_name} interviewerEmail={data.interviewer_email} />;

  return (
    <SelectionView
      data={data}
      selected={selected}
      onToggle={toggle}
      onSave={submit}
      saving={saving}
      savedAt={savedAt}
      error={saveError}
    />
  );
}
